import React, { useEffect, useMemo, useRef, useState } from "react"
import ReactApexChart from "react-apexcharts"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, FilterSegment, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl, formatPeriodLabel } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

const tabs = [
	{ id: "overview", label: "Обзор", icon: "uil-chart-line" },
	{ id: "speed", label: "Скорость", icon: "uil-stopwatch" },
	{ id: "rating", label: "Рейтинг", icon: "uil-star" },
	{ id: "products", label: "Топ блюд", icon: "uil-utensils" },
	{ id: "heatmap", label: "Тепловая карта", icon: "uil-fire" },
	{ id: "couriers", label: "Курьеры", icon: "uil-bicycle" },
]

const compareOptions = [
	{ id: "year", label: "Год назад" },
	{ id: "period", label: "Пред. период" },
]

const groupOptions = [
	{ id: "hours", label: "По часам" },
	{ id: "dayparts", label: "По дейпартам" },
]

const limitOptions = [
	{ id: 5, label: "Топ-5" },
	{ id: 10, label: "Топ-10" },
	{ id: 20, label: "Топ-20" },
]

const CHANNEL_COLORS = ["#e23b3b", "#f1b733", "#2ecc71", "#4c8df0", "#9b59b6"]

const HEAT_COLORS = [
	{ background: "var(--dark-tint-success, #e4f8ec)", color: "var(--dark-fg-success, #2f9e63)" },
	{ background: "#4cc47c", color: "#ffffff" },
	{ background: "#e7b73c", color: "#ffffff" },
	{ background: "#ec7d3b", color: "#ffffff" },
	{ background: "#e0492f", color: "#ffffff" },
]

const PEAK_DECORATIONS = ["🔥", "⚡", ""]

const medalColors = ["#f0b429", "#9aa5b1", "#cd7f32"]

// Порог «мелких» курьеров для кнопки «Скрыть курьеров < 5 доставок»
const MIN_COURIER_DELIVERIES = 5

const SLA_MIN = 5
const SLA_MAX = 240

function clampSla(value) {
	const number = Number(value)
	if (!number) return 30
	return Math.min(SLA_MAX, Math.max(SLA_MIN, Math.round(number)))
}

function isEmpty(value) {
	return value === null || value === undefined
}

// Минуты: null — «—» (нет доставок со временем)
function formatMinutes(value, digits = 1) {
	return isEmpty(value) ? "—" : `${formatMoney(value, digits)} мин`
}

function formatPercent(value, digits = 1) {
	return isEmpty(value) ? "—" : `${formatMoney(value, digits)}%`
}

function formatNumber(value, digits = 1) {
	return isEmpty(value) ? "—" : formatMoney(value, digits)
}

function metricActual(metric) {
	return metric && typeof metric === "object" ? metric.actual : metric
}

function formatGrowth(value) {
	const number = Number(value)
	return `${number > 0 ? "+" : ""}${number.toFixed(1).replace(".0", "")}%`
}

function lateColor(value) {
	const number = Number(value || 0)
	if (number >= 10) return "#d23434"
	if (number >= 6) return "#e0a800"
	return "#1f9d57"
}

function healthStyle(value) {
	const number = Number(value || 0)
	if (number >= 85) return { background: "var(--dark-tint-success, #e7f8ef)", color: "var(--dark-fg-success, #1f9d57)" }
	if (number >= 70) return { background: "var(--dark-tint-warning, #fdf3da)", color: "var(--dark-fg-warning, #c79100)" }
	return { background: "var(--dark-tint-warning, #fdeede)", color: "#d8772a" }
}

// Пояснение, какие доставки не попали в метрики времени (показываем только ненулевые части)
function timeExclusionNote(timing) {
	const parts = []
	if (Number(timing?.excluded_out_of_range) > 0) parts.push(`${formatMoney(timing.excluded_out_of_range, 0)} вне 0–180 мин`)
	if (Number(timing?.excluded_aggregator) > 0) parts.push(`${formatMoney(timing.excluded_aggregator, 0)} без своего курьера`)
	if (Number(timing?.excluded_preorder) > 0) parts.push(`${formatMoney(timing.excluded_preorder, 0)} предзаказов`)
	const note = "Время — от открытия до закрытия чека, своя курьерская доставка."
	return parts.length ? `${note} Исключено: ${parts.join(", ")}` : note
}

function formatDaypartLabel(column) {
	if (column?.display_label) return column.display_label
	return column?.time_label ? `${column.label} (${column.time_label})` : column?.label
}

function heatLevel(value, min, max) {
	const number = Number(value || 0)
	if (number <= 0) return 0
	if (max <= min) return 2
	const ratio = (number - min) / (max - min)
	return Math.min(4, Math.max(0, Math.floor(ratio * 5)))
}

function shortMoneyAxis(value) {
	return formatMoney(Math.round(value), 0)
}

function Note({ children }) {
	if (!children) return null
	return <div className="mb-3" style={{ ...mutedStyle, fontSize: "11px" }}>{children}</div>
}

// Рост к базе сравнения: цвет — по is_good (хорошо/плохо по смыслу метрики), стрелка — по знаку
function GrowthBadge({ metric }) {
	if (!metric || typeof metric !== "object") return null
	const growth = metric.growth_percent

	let label
	if (isEmpty(growth)) {
		label = Number(metric.actual) > 0 ? "новое" : "—"
	} else {
		const arrow = growth > 0 ? "↗" : growth < 0 ? "↘" : "→"
		label = `${arrow} ${formatGrowth(growth)}`
	}

	let colors = { color: "var(--dark-text-muted, #6b7280)", background: "var(--dark-surface-2, #f1f4f8)" }
	if (metric.is_good === true) colors = { color: "var(--dark-fg-success, #1f9d57)", background: "var(--dark-tint-success, #e7f8ef)" }
	if (metric.is_good === false) colors = { color: "var(--dark-fg-danger, #d23434)", background: "var(--dark-tint-danger, #fdecec)" }

	return (
		<span style={{ ...colors, borderRadius: "12px", padding: "2px 7px", fontWeight: 600 }}>
			{label}
		</span>
	)
}

function DeliveryKpi({ title, value, sublabel, metric, compareLabel, icon }) {
	return (
		<div className="card h-100 mb-0" style={cardStyle}>
			<div className="card-body py-3 px-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={mutedStyle}>{title}</div>
					<i className={`uil ${icon} text-muted`} style={{ fontSize: "16px" }}></i>
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "24px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
				<div className="d-flex align-items-center gap-2 mt-2" style={{ fontSize: "11px" }}>
					<GrowthBadge metric={metric} />
					{compareLabel && <span className="text-muted">{compareLabel}</span>}
				</div>
			</div>
		</div>
	)
}

function RevenueDynamics({ dynamics }) {
	const categories = dynamics?.categories || []
	const revenueSeries = (dynamics?.series || []).find(item => item.key === "revenue") || dynamics?.series?.[0]
	const series = [{ name: revenueSeries?.name || "Выручка доставки", data: revenueSeries?.data || [] }]

	const options = {
		chart: { type: "area", height: 280, toolbar: { show: false }, fontFamily: "inherit" },
		colors: ["#e23b3b"],
		dataLabels: { enabled: false },
		stroke: { curve: "smooth", width: 2 },
		fill: {
			type: "gradient",
			gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100] },
		},
		grid: { borderColor: "#eef1f5", strokeDashArray: 4 },
		xaxis: {
			categories,
			tickAmount: 8,
			labels: { style: { colors: "#8791a1", fontSize: "11px" } },
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		yaxis: {
			labels: {
				style: { colors: "#8791a1", fontSize: "11px" },
				formatter: (value) => shortMoneyAxis(value),
			},
		},
		tooltip: { y: { formatter: (value) => `${formatMoney(value, 0)}` } },
	}

	return (
		<div className="card h-100 mb-0" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3 d-flex align-items-center gap-2">
					<i className="uil uil-chart-line"></i>
					Динамика доставки
				</h5>
				<ReactApexChart options={options} series={series} type="area" height={280} />
			</div>
		</div>
	)
}

function DeliveryChannels({ channels }) {
	const data = channels?.data || []
	const totalCheques = channels?.total_cheques || 0
	const series = data.map(item => Number(item.revenue || 0))
	const labels = data.map(item => item.name)

	const options = {
		chart: { type: "donut", fontFamily: "inherit" },
		labels,
		colors: CHANNEL_COLORS,
		legend: { show: false },
		dataLabels: { enabled: false },
		stroke: { width: 0 },
		plotOptions: {
			pie: {
				donut: {
					size: "72%",
					labels: {
						show: true,
						total: {
							show: true,
							label: "ЗАКАЗОВ",
							fontSize: "12px",
							color: "var(--dark-text-muted, #8791a1)",
							formatter: () => formatMoney(totalCheques, 0),
						},
						value: { fontSize: "22px", fontWeight: 800, color: "#111827" },
					},
				},
			},
		},
		tooltip: { y: { formatter: (value) => `${formatMoney(value, 0)}` } },
	}

	return (
		<div className="card h-100 mb-0" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3 d-flex align-items-center gap-2">
					<i className="uil uil-clock-three"></i>
					Каналы доставки
				</h5>
				{series.length > 0 ? (
					<ReactApexChart options={options} series={series} type="donut" height={230} />
				) : (
					<div className="text-muted text-center py-5">Нет данных</div>
				)}
				<div className="mt-3">
					{data.map((item, index) => (
						<div className="d-flex align-items-center justify-content-between py-1" key={item.name}>
							<div className="d-flex align-items-center gap-2">
								<span style={{ width: "10px", height: "10px", borderRadius: "3px", background: CHANNEL_COLORS[index % CHANNEL_COLORS.length] }}></span>
								<span style={{ fontSize: "13px" }}>{item.name}</span>
							</div>
							<div className="d-flex align-items-center gap-3">
								<b style={{ fontSize: "13px" }}>{item.share_percent}%</b>
								<span style={mutedStyle}>{formatMoney(item.revenue, 0)}</span>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

function BranchList({ branches }) {
	const maxRevenue = useMemo(
		() => branches.reduce((max, item) => Math.max(max, Number(item.revenue || 0)), 0),
		[branches]
	)

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center mb-3">
					<h5 className="mb-0 d-flex align-items-center gap-2">
						<i className="uil uil-shop"></i>
						Доставки по филиалам
					</h5>
					<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "12px", padding: "3px 10px" }}>
						{branches.length} филиалов
					</span>
				</div>
				{branches.length === 0 && <div className="text-muted text-center py-4">Нет данных за выбранный период</div>}
				{branches.map((branch, index) => (
					<div className="d-flex align-items-center gap-3 py-2" key={branch.pos_id} style={{ borderTop: index === 0 ? "none" : "1px solid var(--dark-border, #f1f4f8)" }}>
						<div
							className="d-flex align-items-center justify-content-center fw-bold"
							style={{
								width: "26px",
								minWidth: "26px",
								fontSize: "12px",
								color: index < 3 ? "#fff" : "var(--dark-text-muted, #8791a1)",
								background: index < 3 ? medalColors[index] : "transparent",
								borderRadius: "50%",
								height: "26px",
							}}
						>
							{index < 3 ? <i className="uil uil-award"></i> : index + 1}
						</div>
						<div className="flex-grow-1" style={{ minWidth: 0 }}>
							<div className="d-flex justify-content-between align-items-center mb-1">
								<span className="text-truncate" style={{ fontWeight: 600 }}>{branch.pos_name}</span>
								<div className="d-flex align-items-center gap-3 flex-shrink-0">
									<span style={{ fontWeight: 600 }}>{formatMoney(branch.revenue, 0)}</span>
									<span style={mutedStyle} title="Медиана времени доставки">
										<i className="uil uil-clock"></i> {formatMinutes(branch.median_delivery_minutes)}
									</span>
									<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "10px", padding: "2px 8px", fontWeight: 600, color: "var(--dark-text-strong, #1f2937)" }}>
										{formatMoney(branch.cheques, 0)} чеков
									</span>
								</div>
							</div>
							<div className="progress" style={{ height: "6px", borderRadius: "10px", background: "var(--dark-surface-2, #f1f4f8)" }}>
								<div
									className="progress-bar"
									style={{
										width: `${maxRevenue > 0 ? (Number(branch.revenue) / maxRevenue) * 100 : 0}%`,
										background: index < 3 ? "#e23b3b" : "var(--dark-tint-primary, #c6ccd6)",
									}}
								></div>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

function Overview({ data, compareMode, onCompareModeChange }) {
	const summary = data.summary || {}
	const timing = data.timing || {}
	const compareLabel = data.compare_label || "к пр. году"
	const comparePeriod = formatPeriodLabel(data.compare_start_date, data.compare_end_date)
	const returns = Number(data.returns || 0)
	const medianMinutes = metricActual(summary.median_delivery_minutes)
	const slaPercent = metricActual(summary.sla_percent)

	const cards = [
		{
			title: "Выручка доставки",
			value: formatMoney(metricActual(summary.revenue), 0),
			sublabel: returns > 0 ? `за вычетом возвратов (${formatMoney(returns, 0)})` : "за вычетом возвратов",
			metric: summary.revenue,
			icon: "uil-money-bill",
		},
		{
			title: "Чеки",
			value: formatMoney(metricActual(summary.cheques), 0),
			sublabel: "Количество заказов",
			metric: summary.cheques,
			icon: "uil-receipt",
		},
		{
			title: "Средний чек",
			value: formatMoney(metricActual(summary.average_cheque), 0),
			sublabel: "Сумма на заказ",
			metric: summary.average_cheque,
			icon: "uil-chart-pie",
		},
		{
			title: "Время доставки",
			value: isEmpty(medianMinutes) ? "—" : `${formatMoney(medianMinutes, 1)} мин`,
			sublabel: `среднее ${formatNumber(timing.average_minutes)} · p90 ${formatNumber(timing.p90_minutes)} мин`,
			metric: summary.median_delivery_minutes,
			icon: "uil-stopwatch",
		},
		{
			title: "В срок",
			value: formatPercent(slaPercent),
			sublabel: `≤ ${data.sla_minutes} мин`,
			metric: summary.sla_percent,
			icon: "uil-check-circle",
		},
		{
			title: "Доля доставки",
			value: formatPercent(metricActual(summary.delivery_share_percent)),
			sublabel: "От общей выручки",
			metric: summary.delivery_share_percent,
			icon: "uil-clock",
		},
		{
			title: "Глубина чека",
			value: formatNumber(metricActual(summary.cheque_depth)),
			sublabel: "Среднее кол-во блюд в чеке",
			metric: summary.cheque_depth,
			icon: "uil-layer-group",
		},
	]

	return (
		<>
			<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
				<span style={mutedStyle}>
					Сравнение {compareLabel}{comparePeriod ? `: ${comparePeriod}` : ""}
				</span>
				<FilterSegment options={compareOptions} value={compareMode} onChange={onCompareModeChange} />
			</div>

			<div className="mb-2" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(175px, 1fr))", gap: "12px" }}>
				{cards.map(card => (
					<DeliveryKpi key={card.title} {...card} compareLabel={compareLabel} />
				))}
			</div>
			<Note>{timeExclusionNote(timing)}</Note>

			<div className="row g-3 mb-3">
				<div className="col-xl-8">
					<RevenueDynamics dynamics={data.dynamics} />
				</div>
				<div className="col-xl-4">
					<DeliveryChannels channels={data.channels} />
				</div>
			</div>

			<BranchList branches={data.branches || []} />
		</>
	)
}

function SpeedKpi({ title, value, sublabel, valueColor = "var(--dark-text-strong, #111827)", icon }) {
	return (
		<div className="card h-100 mb-0" style={cardStyle}>
			<div className="card-body py-3 px-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={mutedStyle}>{title}</div>
					<i className={`uil ${icon} text-muted`} style={{ fontSize: "16px" }}></i>
				</div>
				<h3 className="mb-0 mt-3" style={{ fontSize: "26px", fontWeight: 800, color: valueColor, letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function SpeedDynamics({ dynamics }) {
	const categories = dynamics?.categories || []
	const seriesData = (key) => (dynamics?.series || []).find(item => item.key === key)?.data || []
	const series = [
		{ name: "Медиана", type: "line", data: seriesData("median") },
		{ name: "Среднее", type: "line", data: seriesData("average") },
		{ name: "Доля опозданий", type: "line", data: seriesData("late_share") },
	]
	const minutesLabel = (value) => (isEmpty(value) ? "—" : Math.round(value))

	const options = {
		chart: { type: "line", height: 300, toolbar: { show: false }, fontFamily: "inherit" },
		colors: ["#4c8df0", "#9aa5b1", "#e23b3b"],
		dataLabels: { enabled: false },
		stroke: { curve: "smooth", width: [2, 2, 2], dashArray: [0, 0, 4] },
		grid: { borderColor: "#eef1f5", strokeDashArray: 4 },
		legend: { position: "bottom", markers: { radius: 12 } },
		xaxis: {
			categories,
			tickAmount: 12,
			labels: { style: { colors: "#8791a1", fontSize: "11px" } },
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		// Минуты — левая ось (медиана и среднее на одной), доля опозданий — правая ось в процентах
		yaxis: [
			{
				seriesName: "Медиана",
				min: 0,
				title: { text: "мин", style: { color: "#8791a1", fontWeight: 500 } },
				labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: minutesLabel },
			},
			{ seriesName: "Медиана", show: false, min: 0, labels: { formatter: minutesLabel } },
			{
				seriesName: "Доля опозданий",
				opposite: true,
				min: 0,
				max: 100,
				labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => (isEmpty(value) ? "—" : `${Math.round(value)}%`) },
			},
		],
		tooltip: {
			shared: true,
			y: [
				{ formatter: (value) => formatMinutes(value) },
				{ formatter: (value) => formatMinutes(value) },
				{ formatter: (value) => formatPercent(value) },
			],
		},
	}

	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3 d-flex align-items-center gap-2">
					<i className="uil uil-chart-line"></i>
					Динамика скорости
				</h5>
				<ReactApexChart options={options} series={series} type="line" height={300} />
			</div>
		</div>
	)
}

function SpeedBranchTable({ branches }) {
	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3 d-flex align-items-center gap-2">
					<i className="uil uil-table"></i>
					Скорость по филиалам
				</h5>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>#</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Филиал</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Доставок</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>С временем</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Медиана, мин</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Среднее, мин</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>p90, мин</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>% опозданий</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Ср. опоздание, мин</th>
							</tr>
						</thead>
						<tbody>
							{branches.length === 0 && (
								<tr><td colSpan="9" className="text-muted text-center py-4">Нет данных за выбранный период</td></tr>
							)}
							{branches.map((branch, index) => {
								const borderBottom = index === branches.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={branch.pos_id}>
										<td className="py-3 text-muted" style={{ borderBottom }}>{index + 1}</td>
										<td className="py-3" style={{ borderBottom, fontWeight: 600 }}>{branch.pos_name}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(branch.deliveries, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(branch.timed, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom, fontWeight: 600 }}>{formatNumber(branch.median_minutes)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatNumber(branch.average_minutes)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatNumber(branch.p90_minutes)}</td>
										<td className="py-3 text-end" style={{ borderBottom, fontWeight: 600 }}>{formatPercent(branch.late_share_percent)}</td>
										<td className="py-3 text-end" style={{ borderBottom, color: isEmpty(branch.average_late_minutes) ? undefined : lateColor(branch.average_late_minutes), fontWeight: 600 }}>
											{formatNumber(branch.average_late_minutes)}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function Speed({ data }) {
	const summary = data.summary || {}

	return (
		<>
			<div className="mb-2" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "12px" }}>
				<SpeedKpi title="Доставок" value={formatMoney(summary.deliveries, 0)} sublabel={`с временем: ${formatMoney(summary.timed, 0)}`} icon="uil-box" />
				<SpeedKpi title="Медиана" value={formatMinutes(summary.median_minutes)} sublabel="от открытия до закрытия чека" icon="uil-stopwatch" />
				<SpeedKpi title="p90" value={formatMinutes(summary.p90_minutes)} sublabel={`среднее ${formatMinutes(summary.average_minutes)}`} icon="uil-chart-bar" />
				<SpeedKpi title="% опозданий" value={formatPercent(summary.late_share_percent)} sublabel={`дольше ${data.sla_minutes} мин`} valueColor="#e0a800" icon="uil-arrow-growth" />
				<SpeedKpi
					title="Ср. опоздание"
					value={formatMinutes(summary.average_late_minutes)}
					valueColor={isEmpty(summary.average_late_minutes) ? "#111827" : lateColor(summary.average_late_minutes)}
					icon="uil-exclamation-triangle"
				/>
			</div>
			<Note>{timeExclusionNote(summary)}</Note>

			<SpeedDynamics dynamics={data.dynamics} />
			<SpeedBranchTable branches={data.branches || []} />
		</>
	)
}

function CourierRatingTable({ couriers }) {
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3 d-flex align-items-center gap-2">
					<i className="uil uil-award"></i>
					Рейтинг курьеров
				</h5>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>#</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Курьер</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Филиал</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Доставок</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Медиана, мин</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Среднее, мин</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>% опозданий</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Ср. опоздание, мин</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Здоровье</th>
							</tr>
						</thead>
						<tbody>
							{couriers.length === 0 && (
								<tr><td colSpan="9" className="text-muted text-center py-4">Нет курьеров за выбранный период</td></tr>
							)}
							{couriers.map((courier, index) => {
								const borderBottom = index === couriers.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								const badge = healthStyle(courier.health)
								return (
									<tr key={courier.courier_key || index}>
										<td className="py-3 text-muted" style={{ borderBottom }}>{index + 1}</td>
										<td className="py-3" style={{ borderBottom, fontWeight: 600 }}>{courier.courier_name}</td>
										<td className="py-3 text-muted" style={{ borderBottom }}>{courier.pos_name}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											{formatMoney(courier.deliveries, 0)}
											{Number(courier.timed) !== Number(courier.deliveries) && !isEmpty(courier.timed) && (
												<div style={mutedStyle}>с временем {formatMoney(courier.timed, 0)}</div>
											)}
										</td>
										<td className="py-3 text-end" style={{ borderBottom, fontWeight: 600 }}>{formatNumber(courier.median_path_minutes)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatNumber(courier.average_path_minutes)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatPercent(courier.late_share_percent)}</td>
										<td className="py-3 text-end" style={{ borderBottom, color: lateColor(courier.average_late_minutes), fontWeight: 600 }}>
											{formatNumber(courier.average_late_minutes)}
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											<span style={{ ...badge, borderRadius: "12px", padding: "3px 10px", fontWeight: 700, fontSize: "12px" }}>
												{courier.health}
											</span>
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function AttentionSection({ attention }) {
	if (!attention || attention.length === 0) return null
	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3 d-flex align-items-center gap-2" style={{ color: "#d8772a" }}>
					<i className="uil uil-exclamation-triangle"></i>
					Нужно внимание
				</h5>
				<div className="row g-3">
					{attention.map((courier, index) => (
						<div className="col-md-6" key={courier.courier_key || index}>
							<div className="p-3 rounded-3 h-100" style={{ border: "1px solid var(--dark-tint-border-warning, #f3e3d2)", background: "var(--dark-surface-2, #fffaf4)" }}>
								<div className="d-flex align-items-center gap-2" style={{ fontWeight: 700, color: "#d8772a" }}>
									<i className="uil uil-exclamation-triangle"></i>
									{courier.courier_name}
								</div>
								<div style={mutedStyle}>{courier.pos_name}</div>
								<div className="mt-2" style={{ fontSize: "13px" }}>
									{formatMoney(courier.deliveries, 0)} доставок · ср. опоздание {formatMinutes(courier.average_late_minutes)}
								</div>
								<div className="mt-2" style={{ fontSize: "13px" }}>
									Потерянное время: <b style={{ color: "var(--dark-fg-danger, #d23434)" }}>{formatMoney(courier.lost_minutes, 0)} мин</b>
								</div>
								<div style={mutedStyle}>≈ {formatMoney(courier.lost_hours, 0)} часов ожидания клиентов</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

function Rating({ data, hideLow, onToggleLow }) {
	const couriers = data.couriers || []
	const visible = hideLow ? couriers.filter(courier => Number(courier.deliveries || 0) >= MIN_COURIER_DELIVERIES) : couriers

	return (
		<>
			<div className="d-flex flex-wrap align-items-center gap-3 mb-3">
				<button
					type="button"
					className="btn btn-sm d-inline-flex align-items-center gap-2"
					style={{
						borderRadius: "10px",
						border: hideLow ? "1px solid var(--bs-primary)" : "1px solid var(--dark-border, #dfe5ec)",
						background: hideLow ? "rgba(var(--bs-primary-rgb), 0.1)" : "#fff",
						color: hideLow ? "var(--bs-primary)" : "var(--dark-text-muted, #5b6472)",
						fontWeight: 600,
					}}
					onClick={onToggleLow}
				>
					<i className={`uil ${hideLow ? "uil-check-circle" : "uil-circle"}`}></i>
					Скрыть курьеров &lt; {MIN_COURIER_DELIVERIES} доставок
				</button>
				{hideLow && couriers.length > visible.length && (
					<span style={mutedStyle}>скрыто: {couriers.length - visible.length}</span>
				)}
			</div>
			<Note>{data.time_note}</Note>

			<CourierRatingTable couriers={visible} />
			<AttentionSection attention={data.attention || []} />
		</>
	)
}

function redShades(count) {
	const shades = []
	for (let i = 0; i < count; i++) {
		const t = count > 1 ? i / (count - 1) : 0
		const r = Math.round(192 + (240 - 192) * t)
		const g = Math.round(36 + (179 - 36) * t)
		const b = Math.round(31 + (176 - 31) * t)
		shades.push(`rgb(${r}, ${g}, ${b})`)
	}
	return shades
}

const productMetricOptions = [
	{ id: "revenue", label: "Выручка" },
	{ id: "quantity", label: "Кол-во" },
]

function TopProductsChart({ products }) {
	const [metric, setMetric] = useState("revenue")
	const [limit, setLimit] = useState(10)

	const items = useMemo(() => products.slice(0, limit), [products, limit])
	const categories = items.map(item => item.product_name)
	const series = [{ name: metric === "revenue" ? "Выручка" : "Кол-во", data: items.map(item => Number(item[metric] || 0)) }]
	const colors = redShades(items.length)

	const options = {
		chart: { type: "bar", height: Math.max(260, items.length * 34), toolbar: { show: false }, fontFamily: "inherit" },
		colors,
		plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 4, barHeight: "70%" } },
		dataLabels: { enabled: false },
		legend: { show: false },
		grid: { borderColor: "#eef1f5", strokeDashArray: 4 },
		xaxis: {
			categories,
			labels: {
				style: { colors: "#8791a1", fontSize: "11px" },
				formatter: (value) => (metric === "revenue" ? shortMoneyAxis(value, 0) : Math.round(value)),
			},
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		yaxis: { labels: { style: { colors: "#5b6472", fontSize: "12px" } } },
		tooltip: { y: { formatter: (value) => (metric === "revenue" ? `${formatMoney(value, 0)}` : `${formatMoney(value, 0)} шт`) } },
	}

	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
					<div>
						<h5 className="mb-0 d-flex align-items-center gap-2">
							<i className="uil uil-utensils"></i>
							Топ блюд в доставке
						</h5>
						<div style={mutedStyle}>Выручка — за вычетом возвратов</div>
					</div>
					<div className="d-flex align-items-center flex-wrap gap-2">
						<FilterSegment options={productMetricOptions} value={metric} onChange={setMetric} />
						<FilterSegment options={limitOptions} value={limit} onChange={setLimit} />
					</div>
				</div>
				{items.length > 0 ? (
					<ReactApexChart options={options} series={series} type="bar" height={Math.max(260, items.length * 34)} />
				) : (
					<div className="text-muted text-center py-5">Нет данных для отображения</div>
				)}
			</div>
		</div>
	)
}

function TopProductHourly({ hourly }) {
	const [metric, setMetric] = useState("quantity")
	const data = hourly?.data || []
	const hasData = data.some(item => Number(item.revenue || 0) > 0 || Number(item.quantity || 0) > 0)

	const series = [{ name: metric === "revenue" ? "Выручка" : "Кол-во", data: data.map(item => Number(item[metric] || 0)) }]
	const options = {
		chart: { type: "bar", height: 300, toolbar: { show: false }, fontFamily: "inherit" },
		colors: ["#e23b3b"],
		plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
		dataLabels: { enabled: false },
		grid: { borderColor: "#eef1f5", strokeDashArray: 4 },
		xaxis: {
			categories: data.map(item => item.label),
			labels: { style: { colors: "#8791a1", fontSize: "11px" } },
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		yaxis: {
			labels: {
				style: { colors: "#8791a1", fontSize: "11px" },
				formatter: (value) => (metric === "revenue" ? shortMoneyAxis(value) : Math.round(value)),
			},
		},
		tooltip: { y: { formatter: (value) => (metric === "revenue" ? `${formatMoney(value, 0)}` : `${formatMoney(value, 0)} шт`) } },
	}

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
					<h5 className="mb-0 d-flex align-items-center gap-2">
						<i className="uil uil-clock"></i>
						Топ блюдо по часам
						{hourly?.product_name && <span style={mutedStyle}>· {hourly.product_name}</span>}
					</h5>
					<FilterSegment options={[...productMetricOptions].reverse()} value={metric} onChange={setMetric} />
				</div>
				{hasData ? (
					<ReactApexChart options={options} series={series} type="bar" height={300} />
				) : (
					<div className="text-center py-5">
						<i className="uil uil-clock text-muted" style={{ fontSize: "32px" }}></i>
						<div className="mt-2" style={{ fontWeight: 600 }}>Нет данных для отображения</div>
					</div>
				)}
			</div>
		</div>
	)
}

function Products({ data }) {
	return (
		<>
			<TopProductsChart products={data.products || []} />
			<TopProductHourly hourly={data.hourly} />
		</>
	)
}

// Ячейка — среднее число заказов за один такой день недели (value), всего за период — cheques
function HeatGrid({ data }) {
	const columns = data.columns || []
	const rows = data.rows || []
	const isDayparts = data.group === "dayparts"
	const heatValues = rows.flatMap(row => row.cells.map(cell => Number(cell.value || 0))).filter(value => value > 0)
	const min = heatValues.length ? Math.min(...heatValues) : 0
	const max = heatValues.length ? Math.max(...heatValues) : 0

	if (columns.length === 0 || rows.length === 0) {
		return <div className="text-muted text-center py-5">Нет данных за выбранный период</div>
	}

	const columnLabel = (column) => (isDayparts ? formatDaypartLabel(column) : column?.label || "")

	return (
		<div style={{ overflowX: "auto" }}>
			<table style={{ borderCollapse: "separate", borderSpacing: "4px", width: "100%" }}>
				<thead>
					<tr>
						<th style={{ width: "44px" }}></th>
						{columns.map(column => (
							<th key={column.key} className="text-center" style={{ ...mutedStyle, fontWeight: 600, padding: "4px 0" }}>
								{columnLabel(column)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map(row => (
						<tr key={row.key}>
							<td style={{ ...mutedStyle, fontWeight: 600, paddingRight: "8px", whiteSpace: "nowrap" }}>{row.label}</td>
							{row.cells.map((cell, index) => {
								const value = Number(cell.value || 0)
								const palette = HEAT_COLORS[heatLevel(value, min, max)]
								return (
									<td key={`${row.key}-${columns[index]?.key || index}`} style={{ padding: 0 }}>
										<div
											title={`${row.label} ${columnLabel(columns[index])}: в среднем ${formatMoney(value, 1)} заказов за день, всего ${formatMoney(cell.cheques, 0)} за период`}
											className="d-flex align-items-center justify-content-center"
											style={{
												height: "34px",
												minWidth: "48px",
												borderRadius: "7px",
												background: palette.background,
												color: palette.color,
												fontSize: "11px",
												fontWeight: 600,
											}}
										>
											{value > 0 ? formatMoney(value, 1) : "—"}
										</div>
									</td>
								)
							})}
						</tr>
					))}
				</tbody>
			</table>

			<div className="d-flex align-items-center gap-2 mt-3" style={mutedStyle}>
				<span>Мало заказов</span>
				<div className="d-flex gap-1">
					{HEAT_COLORS.map((palette, index) => (
						<span key={index} style={{ width: "22px", height: "14px", borderRadius: "4px", background: palette.background }}></span>
					))}
				</div>
				<span>Много заказов</span>
			</div>
		</div>
	)
}

function DeliveryHeatMap({ data, group, onGroupChange }) {
	const peakHours = data.peak_hours || []
	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
					<div>
						<h5 className="mb-0 d-flex align-items-center gap-2">
							<i className="uil uil-table"></i>
							{group === "dayparts" ? "Тепловая карта доставки по дейпартам" : "Тепловая карта доставки по часам"}
						</h5>
						<div style={mutedStyle}>Среднее число заказов доставки за один такой день недели</div>
					</div>
					<FilterSegment options={groupOptions} value={group} onChange={onGroupChange} />
				</div>

				{peakHours.length > 0 && (
					<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
						<span style={mutedStyle}>Пиковые часы доставки:</span>
						{peakHours.map((peak, index) => (
							<span
								key={peak.hour}
								className="d-inline-flex align-items-center gap-1"
								style={{ background: "rgba(var(--bs-primary-rgb), 0.1)", color: "var(--bs-primary)", borderRadius: "14px", padding: "2px 10px", fontSize: "12px", fontWeight: 600 }}
							>
								<i className="uil uil-arrow-growth"></i>
								{peak.label}
								{PEAK_DECORATIONS[index] ? ` ${PEAK_DECORATIONS[index]}` : ""}
							</span>
						))}
					</div>
				)}

				<HeatGrid data={data} />
			</div>
		</div>
	)
}

function courierBarColor(index, count) {
	if (index === 0) return "#f0b429"
	const t = count > 1 ? (index - 1) / (count - 1) : 0
	const r = Math.round(214 + (244 - 214) * t)
	const g = Math.round(52 + (180 - 52) * t)
	const b = Math.round(52 + (176 - 52) * t)
	return `rgb(${r}, ${g}, ${b})`
}

function CourierTrend({ courier, categories }) {
	const series = [{ name: "Время", data: (courier.trend || []).map(value => (Number(value) > 0 ? Number(value) : null)) }]
	const options = {
		chart: { type: "area", height: 180, toolbar: { show: false }, sparkline: { enabled: false }, fontFamily: "inherit" },
		colors: ["#5b6472"],
		dataLabels: { enabled: false },
		stroke: { curve: "smooth", width: 2 },
		fill: { type: "gradient", gradient: { opacityFrom: 0.2, opacityTo: 0.02 } },
		grid: { borderColor: "#eef1f5", strokeDashArray: 4, padding: { left: 10, right: 10 } },
		xaxis: {
			categories,
			tickAmount: 12,
			labels: { style: { colors: "#a0a8b4", fontSize: "10px" } },
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		yaxis: { labels: { style: { colors: "#a0a8b4", fontSize: "10px" }, formatter: (value) => (isEmpty(value) ? "—" : Math.round(value)) } },
		tooltip: { y: { formatter: (value) => formatMinutes(value) } },
	}

	return (
		<div className="pt-2 pb-1">
			<div className="d-flex align-items-center gap-3 mb-1" style={{ fontSize: "12px" }}>
				<span style={mutedStyle}>Max <b style={{ color: "var(--dark-text-strong, #1f2937)" }}>{courier.trend_max}</b></span>
				<span style={mutedStyle}>Avg <b style={{ color: "var(--dark-text-strong, #1f2937)" }}>{courier.trend_avg}</b></span>
				<span style={mutedStyle}>Min <b style={{ color: "var(--dark-text-strong, #1f2937)" }}>{courier.trend_min}</b></span>
			</div>
			<ReactApexChart options={options} series={series} type="area" height={180} />
		</div>
	)
}

function CouriersTab({ data }) {
	const couriers = useMemo(() => data.couriers || [], [data])
	const categories = data.categories || []
	const maxDeliveries = useMemo(
		() => couriers.reduce((max, item) => Math.max(max, Number(item.deliveries || 0)), 0),
		[couriers]
	)
	const [expanded, setExpanded] = useState([])

	useEffect(() => {
		if (couriers.length > 0) setExpanded([couriers[0].courier_key])
	}, [couriers])

	function toggle(key) {
		setExpanded(prev => (prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]))
	}

	return (
		<>
			<Note>{data.time_note}</Note>
			<div className="card" style={cardStyle}>
				<div className="card-body">
					<div className="d-flex justify-content-between align-items-center mb-3">
						<h5 className="mb-0 d-flex align-items-center gap-2">
							<i className="uil uil-bicycle"></i>
							Лучшие курьеры
						</h5>
						<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "12px", padding: "3px 10px" }}>
							{data.total_couriers || couriers.length} курьеров
						</span>
					</div>

					{couriers.length === 0 && <div className="text-muted text-center py-4">Нет курьеров за выбранный период</div>}
					{couriers.map((courier, index) => {
						const isOpen = expanded.includes(courier.courier_key)
						return (
							<div key={courier.courier_key} style={{ borderTop: index === 0 ? "none" : "1px solid var(--dark-border, #f1f4f8)" }}>
								<div className="d-flex align-items-center gap-3 py-2" style={{ cursor: "pointer" }} onClick={() => toggle(courier.courier_key)}>
									<div
										className="d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
										style={{
											width: "26px",
											height: "26px",
											fontSize: "12px",
											color: index < 3 ? "#fff" : "var(--dark-text-muted, #8791a1)",
											background: index < 3 ? medalColors[index] : "transparent",
											borderRadius: "50%",
										}}
									>
										{index < 3 ? <i className="uil uil-award"></i> : index + 1}
									</div>
									<div className="flex-grow-1" style={{ minWidth: 0 }}>
										<div className="d-flex justify-content-between align-items-center gap-3 mb-1">
											<span className="text-truncate">
												<b>{courier.courier_name}</b>
												<span style={{ ...mutedStyle, marginLeft: "8px" }}>{courier.pos_name}</span>
											</span>
											<div className="d-flex align-items-center gap-3 flex-shrink-0" style={{ fontSize: "13px" }}>
												<span style={mutedStyle}>{formatMoney(courier.deliveries, 0)} заказов</span>
												<span
													style={mutedStyle}
													title={`Медиана времени доставки; среднее ${formatMinutes(courier.average_path_minutes)}`}
												>
													<i className="uil uil-clock"></i> {formatMinutes(courier.median_path_minutes)}
												</span>
												<span style={{ fontWeight: 600 }}>{formatMoney(courier.revenue, 0)}</span>
												<i className={`uil ${isOpen ? "uil-angle-up" : "uil-angle-down"} text-muted`}></i>
											</div>
										</div>
										<div className="progress" style={{ height: "6px", borderRadius: "10px", background: "var(--dark-surface-2, #f1f4f8)" }}>
											<div
												className="progress-bar"
												style={{
													width: `${maxDeliveries > 0 ? (Number(courier.deliveries) / maxDeliveries) * 100 : 0}%`,
													background: courierBarColor(index, couriers.length),
												}}
											></div>
										</div>
									</div>
								</div>
								{isOpen && <CourierTrend courier={courier} categories={categories} />}
							</div>
						)
					})}
				</div>
			</div>
		</>
	)
}

function Delivery() {
	// Ответ храним вместе с вкладкой, для которой он пришёл: компонент вкладки рисует только свои данные
	const [result, setResult] = useState({ tab: null, data: null })
	const [hideLowCouriers, setHideLowCouriers] = useState(false)
	const requestRef = useRef(0)

	const filters = useAnalyticsFilters({
		...buildPeriod("30days"),
		pos_id: -1,
		order_type_ids: [3],
		source_ids: [],
		courier_id: -1,
		sla_minutes: 30,
		tab: "overview",
		group: "hours",
		compare_mode: "year",
	}, searchWithFilter)
	const { draft, applied, patch, patchApplied } = filters

	const options = useAnalyticsOptions(["sources", "couriers"], draft.pos_id)

	async function searchWithFilter(filter) {
		// Быстрое переключение вкладок: ответ на старый запрос может прийти позже нового — его игнорируем
		const requestId = ++requestRef.current
		const url = buildAnalyticsUrl("/analytics/delivery", { ...filter, sla_minutes: clampSla(filter.sla_minutes) })
		const response = await GET(url, {}, { loader: true })
		if (requestId !== requestRef.current) return
		if (httpOk(response)) setResult({ tab: filter.tab, data: response.data })
	}

	function handlePosChange(posId) {
		patch({ pos_id: posId, courier_id: -1 })
	}

	const data = result.tab === applied.tab ? result.data : null

	return (
		<>
			<AnalyticsFilterBar
				title="Доставка"
				icon="uil-truck"
				subtitle={data?.branch_count ? `Филиалов в сети: ${data.branch_count}` : undefined}
				filters={filters}
				tabs={tabs}
				activeTab={applied.tab}
				onTabChange={(tab) => patchApplied({ tab })}
			>
				<StandardFields filters={filters} options={options} onPosChange={handlePosChange} />
				<FilterField label="Курьер">
					<FilterSelect options={options.couriers} allLabel="Все курьеры" value={draft.courier_id} onChange={(id) => patch({ courier_id: id })} />
				</FilterField>
				<FilterField label="SLA, мин">
					<input
						type="number"
						className="form-control"
						min={SLA_MIN}
						max={SLA_MAX}
						value={draft.sla_minutes}
						onChange={(e) => patch({ sla_minutes: Number(e.target.value) })}
						onBlur={() => patch({ sla_minutes: clampSla(draft.sla_minutes) })}
						style={{ borderRadius: "10px", height: "38px", borderColor: "var(--dark-border, #dfe5ec)" }}
					/>
				</FilterField>
			</AnalyticsFilterBar>

			{data && applied.tab === "overview" && (
				<Overview data={data} compareMode={applied.compare_mode} onCompareModeChange={(mode) => patchApplied({ compare_mode: mode })} />
			)}
			{data && applied.tab === "speed" && <Speed data={data} />}
			{data && applied.tab === "rating" && (
				<Rating data={data} hideLow={hideLowCouriers} onToggleLow={() => setHideLowCouriers(!hideLowCouriers)} />
			)}
			{data && applied.tab === "products" && <Products data={data} />}
			{data && applied.tab === "heatmap" && (
				<DeliveryHeatMap data={data} group={applied.group} onGroupChange={(group) => patchApplied({ group })} />
			)}
			{data && applied.tab === "couriers" && <CouriersTab data={data} />}
		</>
	)
}

export default Delivery
